import { datadogRum } from '@datadog/browser-rum';
import type { RumEvent, RumInitConfiguration } from '@datadog/browser-rum';
import { isProdOrStaging } from 'stash/utils';

type RumNoiseFields = {
	error?: { message?: string; stack?: string; type?: string };
	message?: string;
};

const isFetchAbortMessage = (message: string): boolean => {
	const normalized = message.replace(/^AbortError:\s*/, '').replace(/\.$/, '');
	return (
		normalized === 'Fetch is aborted' || normalized === 'The user aborted a request'
	);
};

const isAbortedFetch = (errorType: string, message: string): boolean =>
	errorType === 'AbortError' || isFetchAbortMessage(message);

const isUnactionableScriptError = (message: string, stack: string): boolean => {
	if (!message.includes('Script error.')) {
		return false;
	}
	return !stack || !/https?:\/\//.test(stack);
};

export const shouldSendRumEvent = (event: RumNoiseFields): boolean => {
	const msg = event.error?.message || event.message || '';
	const stack = event.error?.stack || '';
	const errorType = event.error?.type || '';

	if (
		isAbortedFetch(errorType, msg) ||
		msg.includes('Warning') ||
		msg.includes('Object Not Found Matching Id') ||
		msg.includes('Failed to connect to MetaMask') ||
		stack.includes('-extension://') ||
		msg.startsWith('intervention:') ||
		isUnactionableScriptError(msg, stack)
	) {
		return false;
	}
	return true;
};

/** Match only https origins under *.stsh.io (not stsh.io embedded in another URL). */
export const STASH_TRACING_URL =
	/^https:\/\/(?:[a-z0-9-]+\.)+stsh\.io(?::\d+)?(?:[/?#]|$)/i;

export const createDatadogRumConfig = (): RumInitConfiguration => ({
	clientToken: 'pubc692d28ef23ddfa4d219ae1baf896929',
	applicationId: '3ce43660-fdfe-499a-9e47-61d4a6c0fa21',
	site: 'datadoghq.com',
	service: 'stash-web',
	version: window.Stash.version,
	env: window.Stash.env,
	sessionSampleRate: 100,
	sessionReplaySampleRate: 50,
	trackUserInteractions: true,
	trackResources: true,
	trackLongTasks: true,
	defaultPrivacyLevel: 'mask',
	allowedTracingUrls: [STASH_TRACING_URL],
	// v7 sends a baggage header by default. Traced origins do not allow it yet.
	propagateTraceBaggage: false,
	enableExperimentalFeatures: ['feature_flags'],
	compressIntakeRequests: true,
	beforeSend: (event: RumEvent) => shouldSendRumEvent(event as RumNoiseFields),
});

export const initDatadogRum = (): void => {
	if (!isProdOrStaging()) {
		return;
	}
	datadogRum.init(createDatadogRumConfig());
};
